/* PG-003 Phase A — Product Detail main section
   <mgh-main-product> Web Component.
   - Color / size / quantity selection
   - Featured image swap on color change + URL ?variant update
   - Add to Cart via /cart/add.js
   - Dispatches `cart:open` (loading) on click, then `cart:updated` with { sections } so existing
     <mgh-cart-drawer> opens. */

class MghMainProduct extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-mgh-pp-form]');
    // [AI] 2026-07-14: 商品全体が購入不可（強制SOLD OUT含む sold_out / coming_soon）の場合、
    // カラー・サイズの選択肢も選べない状態にする（実在庫があっても選択不可）。
    // [AI] 2026-08-03: FT-020 preorder_closed（予約締切〜一般発売待ち）も購入不可扱い。
    this.optionsDisabled = this.form
      ? ['sold_out', 'coming_soon', 'preorder_closed'].includes(this.form.dataset.status)
      : false;
    this.variantInput = this.querySelector('[data-mgh-pp-variant-id]');
    this.quantityInput = this.querySelector('[data-mgh-pp-quantity]');
    this.qtyDecBtn = this.querySelector('[data-mgh-pp-qty="-1"]');
    this.qtyIncBtn = this.querySelector('[data-mgh-pp-qty="1"]');
    this.colorButtons = Array.from(this.querySelectorAll('[data-mgh-pp-color]'));
    this.sizeButtons = Array.from(this.querySelectorAll('[data-mgh-pp-size]'));
    this.colorLabel = this.querySelector('[data-mgh-pp-color-label]');
    this.priceEl = this.querySelector('[data-mgh-pp-price]');
    this.priceInclEl = this.querySelector('[data-mgh-pp-price-incl]');
    this.lowStockEl = this.querySelector('[data-mgh-pp-low-stock]');
    this.lowStockTemplate = this.lowStockEl ? this.lowStockEl.dataset.template || '' : '';
    this.cta = this.querySelector('[data-mgh-pp-cta]');
    this.ctaText = this.querySelector('[data-mgh-pp-cta-text]');
    this.ctaError = this.querySelector('[data-mgh-pp-cta-error]');
    this.slides = Array.from(this.querySelectorAll('[data-mgh-pp-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-mgh-pp-dot]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-mgh-pp-thumb]'));
    this.slidesContainer = this.querySelector('[data-mgh-pp-slides]');
    this.stickyCta = this.querySelector('[data-mgh-pp-sticky-cta]');
    this.stickyPrice = this.querySelector('[data-mgh-pp-sticky-price]');
    this.stickyButton = this.querySelector('[data-mgh-pp-sticky-button]');
    this.stickyText = this.querySelector('[data-mgh-pp-sticky-text]');
    this.stickyThumb = this.querySelector('[data-mgh-pp-sticky-thumb]');
    this.stickyVariant = this.querySelector('[data-mgh-pp-sticky-variant]');
    this.shareCopyBtn = this.querySelector('[data-mgh-pp-share-copy]');
    this.shareCopyMsg = this.querySelector('[data-mgh-pp-share-copy-msg]');
    this.notifyBox = this.querySelector('[data-mgh-pp-notify]');
    // [AI] 2026-08-05: FT-021 選択中バリアントに応じて付与タグを差し替える
    this.notifyTagsInput = this.querySelector('[data-mgh-pp-notify-tags]');
    // Liquid が入れる初期値は商品単位までのタグ。サイズ未確定に戻したときの復帰先。
    this.notifyTagsDefault = this.notifyTagsInput ? this.notifyTagsInput.value : '';
    this.notifySubmittedTags = null;
    // [AI] 2026-08-06: FT-021 送信成功時は Liquid が完了メッセージを出力する。
    // その場合は在庫状況に関わらず通知ボックスを表示したままにする（下記 notifyPosted）。
    this.notifySuccess = this.querySelector('[data-mgh-pp-notify-success]');
    this.notifyPosted = this.notifySuccess !== null;
    this.zoomModal = this.querySelector('[data-mgh-pp-zoom-modal]');
    this.zoomImage = this.querySelector('[data-mgh-pp-zoom-image]');
    this.zoomClose = this.querySelector('[data-mgh-pp-zoom-close]');
    this.zoomThumbsContainer = this.querySelector('[data-mgh-pp-zoom-thumbs]');
    this.zoomStage = this.querySelector('[data-mgh-pp-zoom-stage]');
    this.zoomPrev = this.querySelector('[data-mgh-pp-zoom-prev]');
    this.zoomNext = this.querySelector('[data-mgh-pp-zoom-next]');
    this.sizeGuideDrawer = this.querySelector('[data-mgh-pp-size-guide-drawer]');
    this.sizeGuideTrigger = this.querySelector('[data-mgh-pp-size-guide-trigger]');
    this.sizeGuideClose = this.querySelector('[data-mgh-pp-size-guide-close]');
    this.sizeGuideBackdrop = this.querySelector('[data-mgh-pp-size-guide-backdrop]');
    this.findSizeDrawer = this.querySelector('[data-mgh-pp-find-size-drawer]');
    this.findSizeTrigger = this.querySelector('[data-mgh-pp-find-size-trigger]');
    this.findSizeClose = this.querySelector('[data-mgh-pp-find-size-close]');
    this.findSizeForm = this.querySelector('[data-mgh-pp-find-size-form]');
    this.findSizeResult = this.querySelector('[data-mgh-pp-find-size-result]');
    this.findSizeConfig = this.parseFindSizeConfig();

    this.lowStockThreshold = parseInt(this.dataset.lowStockThreshold, 10) || 3;
    this.enableLowStock = this.dataset.enableLowStock === 'true';
    this.enableStickyCta = this.dataset.enableStickyCta === 'true';

    this.labels = this.parseLabels();
    this.variants = this.parseVariants();
    // [AI] 2026-06-01 FT-009: 「全カラーに代表画像が割り当て済み」のときだけ X モード。
    // X モードでは色選択時に絞り込み（filterGalleryByColor）せず、全画像表示のまま
    // syncVariant が featured_media_index へジャンプする。一部の色だけ未割当の半設定では
    // 画像が動かなくなるため、その場合はレガシーの alt フィルタにフォールバックする。
    // （Liquid 側 has_variant_media と同一定義に揃える）
    const mghColorValues = [
      ...new Set(this.variants.map((v) => v.color).filter(Boolean)),
    ];
    this.hasVariantMedia =
      mghColorValues.length > 0 &&
      mghColorValues.every((c) =>
        this.variants.some(
          (v) =>
            v.color === c &&
            v.featured_media_index != null &&
            v.featured_media_index >= 0,
        ),
      );
    this.state = {
      color: this.dataset.initialColor || '',
      size: this.dataset.initialSize || '',
      quantity: 1,
    };

    this.bindEvents();
    // X モード: 初期 variant の代表画像へ「アニメーションなし」で位置合わせ。
    // activeSlideIndex を先に確定させることで、直後の syncFromState → syncVariant による
    // 再ジャンプ（mobile では smooth スクロール）を抑止し、読込時の横スクロールを防ぐ。
    if (this.hasVariantMedia) {
      const initVariant = this.findVariant();
      if (
        initVariant &&
        initVariant.featured_media_index != null &&
        initVariant.featured_media_index >= 0
      ) {
        this.goToSlide(initVariant.featured_media_index, true);
      }
    }
    this.syncFromState();
    // レガシー（alt フィルタ）モードのみ初期絞り込み。
    if (!this.hasVariantMedia && this.state.color) {
      this.filterGalleryByColor(this.state.color);
    }
    if (this.enableStickyCta && this.stickyCta && this.cta) {
      this.setupStickyCta();
    }
    // [AI] 2026-08-06: FT-021 送信後は {% form 'customer' %} がページを再読み込みするため、
    // syncFromState の結果に関わらず完了メッセージを表示したままにする。
    // [AI] 2026-08-07: 読込直後の自動スクロールは意図しないページ移動に見えるため行わない。
    if (this.notifyPosted && this.notifyBox) {
      this.notifyBox.removeAttribute('hidden');
    }
    // [AI] 2026-08-07: FT-021 {% form 'customer', id: ... %} は action に
    // "#mgh-restock-notify" を付けて出力するため、送信後の再読み込みでブラウザが
    // そのアンカーへジャンプし、勝手にスクロールしたように見える。
    // POST 先としてフラグメントは不要なので取り除く。
    this.notifyForm = this.querySelector('#mgh-restock-notify');
    if (this.notifyForm) {
      const notifyAction = this.notifyForm.getAttribute('action');
      if (notifyAction) {
        this.notifyForm.setAttribute('action', notifyAction.split('#')[0]);
      }
      this.notifyAjaxSuccess = this.querySelector('[data-mgh-pp-notify-ajax-success]');
      this.notifyAjaxError = this.querySelector('[data-mgh-pp-notify-ajax-error]');
      this.notifyForm.addEventListener('submit', (event) => this.handleNotifySubmit(event));
    }
    // [AI] 2026-08-07: FT-021 未ログイン時のログイン導線。
    // Liquid の return_url は request.path のみでバリアントを含まない。
    // URL は選択中バリアントに合わせて replaceState 済みなので、遷移直前に
    // クエリごと入れ直し、ログインから戻ったとき同じサイズが選ばれた状態に復帰させる。
    const notifyLoginLink = this.querySelector('[data-mgh-pp-notify-login]');
    if (notifyLoginLink) {
      // click だけだと中クリックや command+click で新規タブを開いたときに
      // 発火せず、バリアントを含まない URL のまま遷移してしまう。
      // pointerdown はボタン種別を問わず先に発火するのでこちらで書き換える。
      const syncReturnUrl = () => {
        const returnUrl = window.location.pathname + window.location.search;
        notifyLoginLink.href = `${notifyLoginLink.pathname}?return_url=${encodeURIComponent(returnUrl)}`;
      };
      notifyLoginLink.addEventListener('pointerdown', syncReturnUrl);
      notifyLoginLink.addEventListener('click', syncReturnUrl);
    }
  }

  /**
   * [AI] 2026-05-11: 参照カウント方式の body スクロールロック。
   * 複数オーバーレイ（zoom modal / find-size / size-guide）が同時に開いた時、
   * 一方を閉じても他方が開いていればスクロールロックを維持する。
   */
  lockBodyScroll() {
    const count = parseInt(document.body.dataset.mghScrollLock || '0', 10) + 1;
    document.body.dataset.mghScrollLock = String(count);
    document.body.style.overflow = 'hidden';
  }

  unlockBodyScroll() {
    const count = Math.max(0, parseInt(document.body.dataset.mghScrollLock || '0', 10) - 1);
    if (count === 0) {
      delete document.body.dataset.mghScrollLock;
      document.body.style.overflow = '';
    } else {
      document.body.dataset.mghScrollLock = String(count);
    }
  }

  /**
   * [AI] 2026-05-11: dialog フォーカス管理（a11y）。
   * 開く時に dialog 内の最初のフォーカス可能要素へ、閉じる時に trigger へ戻す。
   */
  focusFirstIn(container) {
    if (!container) return;
    requestAnimationFrame(() => {
      const target = container.querySelector(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (target) target.focus();
    });
  }

  parseFindSizeConfig() {
    try {
      const node = this.querySelector('[data-mgh-pp-find-size-config]');
      if (!node) return null;
      const parsed = JSON.parse(node.textContent || '{}');
      // If chart is a JSON string (from section textarea), parse again
      if (typeof parsed.chart === 'string' && parsed.chart.trim().length > 0) {
        try {
          parsed.chart = JSON.parse(parsed.chart);
        } catch (e) {
          parsed.chart = null;
        }
      }
      return parsed;
    } catch (error) {
      return null;
    }
  }

  parseLabels() {
    try {
      const node = this.querySelector('[data-mgh-pp-labels]');
      if (!node) return {};
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  parseVariants() {
    try {
      const node = this.querySelector('[data-mgh-pp-variants]');
      if (!node) return [];
      return JSON.parse(node.textContent || '[]');
    } catch (error) {
      return [];
    }
  }

  bindEvents() {
    this.colorButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.handleColorClick(btn));
    });
    this.sizeButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.handleSizeClick(btn));
    });
    if (this.qtyDecBtn) this.qtyDecBtn.addEventListener('click', () => this.changeQuantity(-1));
    if (this.qtyIncBtn) this.qtyIncBtn.addEventListener('click', () => this.changeQuantity(1));
    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', () => this.handleQuantityInput());
    }
    if (this.form) {
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    }
    this.thumbs.forEach((thumb, idx) => {
      thumb.addEventListener('click', () => this.goToSlide(idx));
    });
    this.dots.forEach((dot, idx) => {
      dot.addEventListener('click', () => this.goToSlide(idx));
    });
    if (this.slidesContainer) {
      this.slidesContainer.addEventListener('scroll', () => this.handleScroll(), { passive: true });
      // [AI] 2026-05-18: スワイプで端まで来たら反対端へループ。
      // ネイティブ scroll-snap を活かしつつ、touchend 後の scrollLeft を見て
      // 端で「もう一手」スワイプされた場合だけ反対端へ smooth scroll する。
      // [AI] 2026-05-19: disconnectedCallback で removeEventListener できるよう
      // ハンドラを this._galleryTouchStart / this._galleryTouchEnd に保持。
      this._galleryTouchStart = (e) => {
        const t = e.touches && e.touches[0];
        if (!t) return;
        this._galleryTouchStartX = t.clientX;
        this._galleryTouchScrollStart = this.slidesContainer.scrollLeft;
      };
      this._galleryTouchEnd = (e) => this.handleGallerySwipeEnd(e);
      this.slidesContainer.addEventListener('touchstart', this._galleryTouchStart, { passive: true });
      this.slidesContainer.addEventListener('touchend', this._galleryTouchEnd, { passive: true });
    }
    // [AI] 2026-05-11: Phase B-3 SNS 共有 - URL コピー
    if (this.shareCopyBtn) {
      this.shareCopyBtn.addEventListener('click', () => this.handleShareCopy());
    }

    // Zoom modal: click on slide to open
    if (this.zoomModal && this.zoomImage) {
      this.slides.forEach((slide) => {
        slide.addEventListener('click', () => this.openZoom(slide));
      });
      if (this.zoomClose) {
        this.zoomClose.addEventListener('click', () => this.closeZoom());
      }
      this.zoomModal.addEventListener('click', (event) => {
        // Clicking outside the image closes the modal
        if (event.target === this.zoomModal) this.closeZoom();
      });
      if (this.zoomPrev) {
        this.zoomPrev.addEventListener('click', (e) => {
          e.stopPropagation();
          this.zoomNavigate(-1);
        });
      }
      if (this.zoomNext) {
        this.zoomNext.addEventListener('click', (e) => {
          e.stopPropagation();
          this.zoomNavigate(1);
        });
      }
      // Find My Size drawer
      if (this.findSizeTrigger && this.findSizeDrawer) {
        this.findSizeTrigger.addEventListener('click', () => {
          this.findSizeTrigger.blur();
          this.openFindSize();
        });
        if (this.findSizeClose) {
          this.findSizeClose.addEventListener('click', () => this.closeFindSize());
        }
        if (this.findSizeForm) {
          this.findSizeForm.addEventListener('submit', (event) => this.handleFindSizeSubmit(event));
        }
        // Button-group selectors for usual size and fit
        this.bindFindSizeButtonGroup('[data-mgh-pp-find-size-usual]', '[data-mgh-pp-find-size-usual-input]');
        this.bindFindSizeButtonGroup('[data-mgh-pp-find-size-fit]', '[data-mgh-pp-find-size-fit-input]');
      }
      // Size Guide drawer
      if (this.sizeGuideTrigger && this.sizeGuideDrawer) {
        this.sizeGuideTrigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.openSizeGuide();
        });
        if (this.sizeGuideClose) {
          this.sizeGuideClose.addEventListener('click', () => this.closeSizeGuide());
        }
        if (this.sizeGuideBackdrop) {
          this.sizeGuideBackdrop.addEventListener('click', () => this.closeSizeGuide());
        }
      }
      // Touch swipe on the stage
      if (this.zoomStage) {
        this.zoomStage.addEventListener('touchstart', (e) => {
          this._zoomTouchStartX = e.touches[0].clientX;
        }, { passive: true });
        this.zoomStage.addEventListener('touchend', (e) => {
          if (this._zoomTouchStartX == null) return;
          const dx = e.changedTouches[0].clientX - this._zoomTouchStartX;
          if (Math.abs(dx) > 50) {
            this.zoomNavigate(dx < 0 ? 1 : -1);
          }
          this._zoomTouchStartX = null;
        });
      }
    }
  }

  handleColorClick(btn) {
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    const color = btn.dataset.mghPpColor;
    if (!color || color === this.state.color) return;
    this.state.color = color;
    // Reset size when color changes (variant availability may differ)
    this.state.size = '';
    // レガシー（alt）モードのみ絞り込み。X モードは絞り込まず、syncFromState 経由で
    // syncVariant が featured_media_index へジャンプする（全画像は表示のまま）。
    if (!this.hasVariantMedia) {
      // Filter gallery before syncing so goToSlide targets visible slides
      this.filterGalleryByColor(color);
    }
    this.syncFromState();
    // SP では色変更後に画像エリアが見えるよう縦スクロール
    if (window.matchMedia('(max-width: 989px)').matches) {
      const gallery = this.querySelector('.mgh-main-product__gallery');
      if (gallery && gallery.scrollIntoView) {
        gallery.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  handleSizeClick(btn) {
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    const size = btn.dataset.mghPpSize;
    if (!size) return;
    this.state.size = size;
    // [AI] 2026-07-22: サイズ選択では画像を動かさない。SP でギャラリーをスワイプした後に
    // サイズを選ぶと先頭画像へ戻る不具合を防ぐため moveGallery=false で同期する。
    this.syncFromState(false);
  }

  // [AI] 2026-07-30 #315: 数量上限は input の max 属性から動的に読む。
  // 通常は 10、予約商品は preorder_max_per_order、無制限予約時は max 属性が無く上限なし。
  // 旧実装は 10 をハードコードしており、予約上限をバイパス/無制限を10で頭打ちしていた。
  maxQuantity() {
    // input の max 属性を上限とする。属性なし（無制限予約）は上限なし扱い。
    // Liquid 側は max に 0/負値を出力しない契約のため、それらも念のため上限なしに倒す。
    const max = this.quantityInput ? parseInt(this.quantityInput.max, 10) : NaN;
    return Number.isFinite(max) && max > 0 ? max : Infinity;
  }

  changeQuantity(delta) {
    const current = parseInt(this.quantityInput.value, 10) || 1;
    const next = Math.max(1, Math.min(this.maxQuantity(), current + delta));
    this.quantityInput.value = next;
    this.state.quantity = next;
    this.syncQuantityButtons();
  }

  handleQuantityInput() {
    const raw = parseInt(this.quantityInput.value, 10);
    const next = Math.max(1, Math.min(this.maxQuantity(), isNaN(raw) ? 1 : raw));
    this.quantityInput.value = next;
    this.state.quantity = next;
    this.syncQuantityButtons();
  }

  syncQuantityButtons() {
    // [AI] 2026-07-14: 商品全体が購入不可なら数量操作を無効化（カラー/サイズ/CTAと統一）
    if (this.optionsDisabled) {
      if (this.qtyDecBtn) this.qtyDecBtn.disabled = true;
      if (this.qtyIncBtn) this.qtyIncBtn.disabled = true;
      if (this.quantityInput) this.quantityInput.disabled = true;
      return;
    }
    if (this.qtyDecBtn) this.qtyDecBtn.disabled = this.state.quantity <= 1;
    if (this.qtyIncBtn) this.qtyIncBtn.disabled = this.state.quantity >= this.maxQuantity();
  }

  syncFromState(moveGallery = true) {
    this.syncColorUI();
    this.syncSizeUI();
    this.syncQuantityButtons();
    const variant = this.findVariant();
    this.syncVariant(variant, moveGallery);
    this.syncCta(variant);
    this.syncLowStock(variant);
  }

  syncColorUI() {
    this.colorButtons.forEach((btn) => {
      // [AI] 2026-07-14: 商品全体が購入不可なら全カラーを sold out（選択不可）表示のまま維持
      if (this.optionsDisabled) {
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('aria-checked', 'false');
        btn.tabIndex = -1;
        return;
      }
      const isActive = btn.dataset.mghPpColor === this.state.color;
      btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      btn.tabIndex = isActive ? 0 : -1;
    });
    if (this.colorLabel) {
      this.colorLabel.textContent = this.state.color || '';
    }
  }

  syncSizeUI() {
    this.sizeButtons.forEach((btn) => {
      const size = btn.dataset.mghPpSize;
      // [AI] 2026-07-14: 商品全体が購入不可なら全サイズを sold out（選択不可）表示のまま維持
      if (this.optionsDisabled) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('aria-checked', 'false');
        btn.title = this.labels.sold_out || '';
        return;
      }
      const variant = this.variants.find(
        (v) => v.color === this.state.color && v.size === size,
      );
      const available = variant ? variant.available : false;
      btn.disabled = !available;
      btn.setAttribute('aria-disabled', available ? 'false' : 'true');
      const isActive = available && this.state.size === size;
      btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      if (!available) {
        btn.title = this.labels.sold_out || '';
      } else {
        btn.removeAttribute('title');
      }
    });
  }

  findVariant() {
    // [AI] 2026-05-21: カラー / サイズの両オプションを持たない商品
    // (トートバッグ等の「Default Title」 1 variant 商品) でも variant を解決できるよう、
    // state.color / state.size が共に空の場合は最初の variant を返す。
    if (!this.state.color && !this.state.size) {
      return this.variants[0] || null;
    }
    if (!this.state.color) {
      // size のみ持つ商品: state.size に一致する variant
      if (this.state.size) {
        return this.variants.find((v) => v.size === this.state.size) || this.variants[0] || null;
      }
      return null;
    }
    if (this.state.size) {
      return (
        this.variants.find(
          (v) => v.color === this.state.color && v.size === this.state.size,
        ) || null
      );
    }
    // No size selected yet — return any variant for current color (for image / price)
    return this.variants.find((v) => v.color === this.state.color) || null;
  }

  syncVariant(variant, moveGallery = true) {
    if (!variant) {
      if (this.variantInput) this.variantInput.value = '';
      return;
    }
    if (this.variantInput) this.variantInput.value = String(variant.id);
    // Update price (main 本体 + 税込 + sticky)
    // [AI] 2026-07-04: メインは本体価格(price_ex_html)、その下に税込(price_html)を表示。
    if (this.priceEl && variant.price_ex_html) {
      this.priceEl.innerHTML = variant.price_ex_html;
    }
    if (this.priceInclEl && variant.price_html) {
      this.priceInclEl.innerHTML = variant.price_html;
    }
    if (this.stickyPrice && variant.price_html) {
      this.stickyPrice.innerHTML = variant.price_html;
    }
    // Update sticky thumb on variant change (color change)
    if (this.stickyThumb && variant.featured_image) {
      this.stickyThumb.src = variant.featured_image;
    }
    // Update sticky variant label (Color / Size)
    if (this.stickyVariant) {
      const parts = [];
      if (this.state.color) parts.push(this.state.color);
      if (this.state.size) parts.push(this.state.size);
      this.stickyVariant.textContent = parts.join(' / ');
    }
    // Swap main image when variant has featured_image
    // 同じスライドのままなら goToSlide を呼ばない（不要なスクロール抑制）
    if (
      moveGallery &&
      variant.featured_media_index != null &&
      variant.featured_media_index >= 0 &&
      this.activeSlideIndex !== variant.featured_media_index
    ) {
      this.goToSlide(variant.featured_media_index);
    }
    // Update URL ?variant=
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);
    } catch (error) {
      // ignore
    }
  }

  syncCta(variant) {
    // [AI] 2026-08-04: FT-020 preorder_closed は CTA 自体が Wishlist ボタン
    // （data-mgh-pp-cta なし）に差し替わるため、ここには入らない。
    if (!this.cta || !this.ctaText) return;
    // [AI] 2026-07-08: FT-016 COMING SOON はグレーアウト・無効・カート不可（在庫/サイズ選択に依らず固定）
    const status = this.form ? this.form.dataset.status : 'normal';
    // [AI] 2026-08-12: 商品個別の CTA 文言（custom.cta_label）。
    // 文言だけの上書きで、押せる/押せないの状態は一切変えない。
    // 優先順位は SOLD OUT の次なので、sold_out 系の分岐では使わない。
    const customCta = this.labels.custom_cta || '';
    if (status === 'coming_soon') {
      this.setCtaState(true, customCta || this.labels.coming_soon || 'COMING SOON');
      this.hideNotify();
      return;
    }
    // [AI] 2026-07-13: 強制SOLD OUT（custom.force_sold_out）。在庫があっても sold_out を優先し購入不可。
    // [AI] 2026-08-03: product_status は強制SOLD OUTと在庫0を同じ 'sold_out' で返すため、
    // data-force-sold-out で区別する。強制のみ再入荷通知を出さない（実在庫があるため）。
    // 在庫0による本来の sold_out は再入荷通知フォームを表示する。
    if (status === 'sold_out') {
      const isForceSoldOut = this.form ? this.form.dataset.forceSoldOut === 'true' : false;
      this.setCtaState(true, this.labels.sold_out || 'Sold Out');
      if (isForceSoldOut) {
        this.hideNotify();
      } else {
        this.showNotify(variant);
      }
      return;
    }
    // [AI] 2026-05-13: FT-006 予約モード時は CTA を「予約する」に固定、sold_out 扱いしない
    const isPreorder = this.form ? this.form.dataset.preorder === 'true' : false;
    // サイズオプション自体が無い商品は size 未選択チェックをスキップ
    const requireSize = this.sizeButtons.length > 0;
    const addLabel =
      customCta ||
      (isPreorder
        ? (this.labels.add_to_cart_preorder || this.labels.add_to_cart || 'Pre-order')
        : (this.labels.add_to_cart || 'Add to Cart'));
    const allSoldOut = this.variants.every((v) => !v.available);
    if (allSoldOut && !isPreorder) {
      this.setCtaState(true, this.labels.sold_out || 'Sold Out');
      this.showNotify(variant);
      return;
    }
    if (!variant || (!variant.available && !isPreorder) || (requireSize && !this.state.size)) {
      const isSoldOut = variant && !variant.available;
      this.setCtaState(true, isSoldOut ? (this.labels.sold_out || 'Sold Out') : (this.labels.select_size || 'Select Size'));
      if (isSoldOut) {
        this.showNotify(variant);
      } else {
        this.hideNotify();
      }
      return;
    }
    this.setCtaState(false, addLabel);
    this.hideNotify();
  }

  /**
   * [AI] 2026-05-11: Phase B-3 再入荷通知の表示/非表示制御
   * Sold Out variant 選択時に通知フォームを表示。
   * [AI] 2026-08-05: FT-021 送信先を {% form 'customer' %} に変更。
   * 顧客に restock タグを付けて待機リスト化するため、選択中バリアントのタグに差し替える。
   * サイズ未選択（variant なし）のときは商品単位のタグ（Liquid が入れた初期値）のままにする。
   */
  showNotify(variant) {
    if (!this.notifyBox) return;
    this.notifyBox.removeAttribute('hidden');
    // 送信済みページ（form.posted_successfully?）では入力欄自体が無い。
    if (!this.notifyTagsInput) return;
    // [AI] 2026-08-07: FT-021 サイズ未確定に戻ったら商品単位のタグへ戻す。
    // 更新しないままだと直前に選んでいたサイズのタグが残り、選んでいない
    // サイズの待機者として登録されてしまう。
    const tags =
      variant && variant.restock_tags ? variant.restock_tags : this.notifyTagsDefault;
    this.notifyTagsInput.value = tags;
    // [AI] 2026-08-07: FT-021 登録済みと別の対象を選び直したらフォームを復帰させる。
    // Ajax 成功時に form を隠したままだと、複数サイズを待ちたい人が
    // 2つ目以降を登録できなくなる。
    if (this.notifySubmittedTags && this.notifySubmittedTags !== tags) {
      this.resetNotifyForm();
    }
  }

  resetNotifyForm() {
    this.notifySubmittedTags = null;
    if (this.notifyForm) this.notifyForm.removeAttribute('hidden');
    if (this.notifyAjaxSuccess) this.notifyAjaxSuccess.setAttribute('hidden', '');
    if (this.notifyAjaxError) this.notifyAjaxError.setAttribute('hidden', '');
    // 成功時はボタンを無効のまま抜けている。ここで戻さないとフォームを
    // 復帰させても押せず、2つ目のサイズを登録できない。
    const button = this.notifyForm
      ? this.notifyForm.querySelector('button[type="submit"]')
      : null;
    if (button) {
      button.disabled = false;
      button.textContent = this.labels.notify_submit || '';
    }
  }

  /**
   * [AI] 2026-08-07: FT-021 再入荷通知を Ajax で送信する。
   * 通常のフォーム送信だとページが再読み込みされ、先頭に戻るかアンカーへ飛ぶかの
   * どちらかになり「押しても何も起きない」ように見える。fetch で送ってその場で
   * 結果を出し分ける。
   *
   * 顧客フォームには JSON API が無く、返ってくるのはリダイレクト先ページの HTML。
   * サーバー側が form.posted_successfully? で描画した完了メッセージの有無で
   * 成否を判定する。エラー時は同じ HTML から default_errors の内容を取り出す。
   */
  async handleNotifySubmit(event) {
    event.preventDefault();
    const button = this.notifyForm.querySelector('button[type="submit"]');
    // 二重送信防止。送信中はボタンを無効化する。
    if (button && button.disabled) return;
    // 通信中にサイズを選び直されると入力値が変わるため、送信時点の値を控える。
    const submittedTags = this.notifyTagsInput ? this.notifyTagsInput.value : '';
    if (button) {
      button.disabled = true;
      button.textContent = this.labels.notify_submitting || '...';
    }
    if (this.notifyAjaxError) this.notifyAjaxError.setAttribute('hidden', '');

    try {
      // FormData をそのまま渡すと multipart/form-data になり、通常のフォーム送信を
      // 前提にしている /contact が 400 を返すことがある。urlencoded に揃える。
      // 応答が返らないとボタンが無効のまま復帰できなくなるため打ち切る。
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      let html;
      try {
        const response = await fetch(this.notifyForm.action, {
          method: 'POST',
          body: new URLSearchParams(new FormData(this.notifyForm)),
          headers: {
            Accept: 'text/html',
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          signal: controller.signal,
        });
        html = await response.text();
      } finally {
        clearTimeout(timeoutId);
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const posted = doc.querySelector('[data-mgh-pp-notify-success]');
      if (posted) {
        this.notifySubmittedTags = submittedTags;
        this.notifyForm.setAttribute('hidden', '');
        if (this.notifyAjaxSuccess) this.notifyAjaxSuccess.removeAttribute('hidden');
        return;
      }
      const errors = doc.querySelector('.mgh-main-product__notify-error:not([data-mgh-pp-notify-ajax-error])');
      this.showNotifyError(errors ? this.parseNotifyErrors(errors) : []);
    } catch (error) {
      this.showNotifyError([]);
    }

    if (button) {
      button.disabled = false;
      button.textContent = this.labels.notify_submit || '';
    }
  }

  /**
   * 取得先ページのエラー表示から文言だけを取り出す。
   * 外部から受け取った HTML をそのまま流し込まないよう、テキストにして扱う。
   */
  parseNotifyErrors(node) {
    const items = Array.from(node.querySelectorAll('li'));
    const source = items.length > 0 ? items : [node];
    return source.map((el) => (el.textContent || '').trim()).filter(Boolean);
  }

  showNotifyError(messages) {
    if (!this.notifyAjaxError) return;
    this.notifyAjaxError.textContent = '';
    if (messages.length > 1) {
      const list = document.createElement('ul');
      messages.forEach((message) => {
        const item = document.createElement('li');
        item.textContent = message;
        list.appendChild(item);
      });
      this.notifyAjaxError.appendChild(list);
    } else {
      this.notifyAjaxError.textContent =
        messages[0] || this.labels.notify_error_generic || '';
    }
    this.notifyAjaxError.removeAttribute('hidden');
  }

  hideNotify() {
    if (!this.notifyBox) return;
    // [AI] 2026-08-06: FT-021 送信完了メッセージは在庫状況に関わらず出し続ける。
    if (this.notifyPosted) return;
    this.notifyBox.setAttribute('hidden', '');
  }

  /**
   * Apply disabled state and label to both the main CTA and the sticky CTA.
   */
  setCtaState(disabled, label) {
    if (this.cta && this.ctaText) {
      this.cta.disabled = disabled;
      this.cta.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      this.ctaText.textContent = label;
    }
    if (this.stickyButton && this.stickyText) {
      this.stickyButton.disabled = disabled;
      this.stickyButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      this.stickyText.textContent = label;
    }
  }

  syncLowStock(variant) {
    if (!this.lowStockEl || !this.enableLowStock) return;
    if (
      variant &&
      variant.available &&
      typeof variant.inventory_quantity === 'number' &&
      variant.inventory_quantity > 0 &&
      variant.inventory_quantity <= this.lowStockThreshold
    ) {
      this.lowStockEl.textContent = this.lowStockTemplate.replace(
        '{count}',
        String(variant.inventory_quantity),
      );
      this.lowStockEl.hidden = false;
    } else {
      this.lowStockEl.textContent = '';
      this.lowStockEl.hidden = true;
    }
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (!this.cta || this.cta.disabled) return;
    const variant = this.findVariant();
    if (!variant || !variant.available) return;

    this.setCtaLoading(true);
    if (this.ctaError) this.ctaError.textContent = '';

    // [AI] 2026-07-23 (FT-017): add.js の応答を待たず、クリック直後にドロワーを
    // ローディング表示で即オープンし体感速度を改善する。応答後に中身を差し替える。
    document.dispatchEvent(new CustomEvent('cart:open', { detail: { loading: true } }));

    try {
      const formData = new FormData();
      formData.append('id', String(variant.id));
      formData.append('quantity', String(this.state.quantity));
      // [AI] 2026-07-23 (FT-017): Bundled Section Rendering でドロワー HTML を同梱取得し、
      // 追加の /cart.js 往復を省く。
      formData.append('sections', 'mgh-cart-drawer');
      formData.append('sections_url', window.location.pathname);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'add failed');
      }
      // [AI] 2026-07-23 (FT-017): add.js 応答に sections（同梱HTML）が含まれる。
      // バッジ件数は同梱HTML内のヘッダー件数から算出するため /cart.js は取得しない（fetch 1本）。
      const addResult = await response.json();
      const sections = addResult && addResult.sections ? addResult.sections : null;
      // [AI] 2026-07-23 (FT-017): openDrawer は付けない。ドロワーはクリック時の
      // cart:open(loading) で既に開いており、ここで再度 open すると、読込中に
      // ユーザーが閉じた場合に勝手に再オープンしてしまうため。
      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { sections } }),
      );
      this.flashCtaSuccess();
    } catch (error) {
      this.setCtaLoading(false);
      // [AI] 2026-07-23 (FT-017): 失敗時は先に開いたドロワーを閉じ、PDP 側にエラー表示
      document.dispatchEvent(new CustomEvent('cart:close'));
      if (this.ctaError) {
        this.ctaError.textContent = (error && error.message) || this.labels.add_error || '';
      }
    }
  }

  setCtaLoading(loading) {
    if (!this.cta || !this.ctaText) return;
    if (loading) {
      this.cta.classList.add('is-loading');
      this.cta.disabled = true;
      this.ctaText.textContent = this.labels.adding || 'Adding...';
      if (this.stickyButton && this.stickyText) {
        this.stickyButton.disabled = true;
        this.stickyText.textContent = this.labels.adding || 'Adding...';
      }
    } else {
      this.cta.classList.remove('is-loading');
      this.cta.disabled = false;
      if (this.stickyButton) this.stickyButton.disabled = false;
    }
  }

  flashCtaSuccess() {
    if (!this.cta || !this.ctaText) return;
    this.cta.classList.remove('is-loading');
    this.cta.disabled = true;
    this.ctaText.textContent = this.labels.added || 'Added';
    if (this.stickyButton && this.stickyText) {
      this.stickyButton.disabled = true;
      this.stickyText.textContent = this.labels.added || 'Added';
    }
    window.clearTimeout(this._ctaResetTimer);
    this._ctaResetTimer = window.setTimeout(() => {
      this.cta.disabled = false;
      if (this.stickyButton) this.stickyButton.disabled = false;
      this.syncCta(this.findVariant());
    }, 1500);
  }

  goToSlide(index, instant) {
    if (index < 0 || index >= this.slides.length) return;
    const target = this.slides[index];
    if (!target || target.hasAttribute('hidden')) return;
    if (this.slidesContainer && this.slides.length > 0) {
      // [AI] 2026-07-07: モバイルはカルーセルなのでスクロール。PC(≥990px)は2列グリッドで
      // 全画像表示のためスクロール不要（横オーバーフロー復活時の誤スクロールも防ぐ）。
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      if (isMobile) {
        if (!instant) {
          target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        } else {
          this.slidesContainer.scrollTo({ left: target.offsetLeft, behavior: 'instant' });
        }
      }
    }
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
    this.activeSlideIndex = index;
  }

  handleScroll() {
    if (!this.slidesContainer) return;
    // [AI] 2026-05-18: ループアニメーション中は dot / thumb の active 計算をスキップ。
    // clone が一時的に挿入されており、scrollLeft / visibleSlides の対応が崩れるため。
    if (this._galleryLooping) return;
    const slideWidth = this.slidesContainer.clientWidth;
    if (slideWidth === 0) return;
    const visibleSlides = this.slides.filter((s) => !s.hasAttribute('hidden'));
    if (visibleSlides.length === 0) return;
    const visIdx = Math.round(this.slidesContainer.scrollLeft / slideWidth);
    const targetSlide = visibleSlides[Math.max(0, Math.min(visIdx, visibleSlides.length - 1))];
    const origIdx = this.slides.indexOf(targetSlide);
    if (origIdx >= 0 && origIdx !== this.activeSlideIndex) {
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === origIdx));
      this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === origIdx));
      this.activeSlideIndex = origIdx;
    }
  }

  /**
   * [AI] 2026-05-18: PDP メイン画像のスワイプを循環させる。
   * 端のスライド（最初 / 最後）で「もう一手」スワイプされた場合、clone を
   * 一時挿入して該当方向に smooth scroll → 着地後に scrollLeft を瞬時に
   * 反対端へ戻し clone を削除。視覚的には連続したスクロール感のまま循環する。
   * touchend 直後に判定して即開始するため、scroll-snap の確定待ちを挟まない
   * （指を離した直後に止まらず連続スクロールする感触を維持する）。
   */
  handleGallerySwipeEnd(event) {
    if (!this.slidesContainer) return;
    const startX = this._galleryTouchStartX;
    const startScroll = this._galleryTouchScrollStart;
    this._galleryTouchStartX = null;
    this._galleryTouchScrollStart = null;
    if (startX == null) return;
    if (this._galleryLooping) return;
    const touch = event.changedTouches && event.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - startX;
    const SWIPE_THRESHOLD = 30;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;

    const visibleSlides = this.slides.filter((s) => !s.hasAttribute('hidden'));
    if (visibleSlides.length < 2) return;
    const slideWidth = this.slidesContainer.clientWidth;
    if (slideWidth === 0) return;
    const maxScroll = (visibleSlides.length - 1) * slideWidth;
    const startedAtRight = Math.abs(startScroll - maxScroll) < 5;
    const startedAtLeft = startScroll < 5;

    // 開始端 + その方向のスワイプ = ループ意図と即判定。
    // scroll-snap がまだ動いていても scrollTo が引き継いで連続感を出す。
    if (dx < -SWIPE_THRESHOLD && startedAtRight) {
      this.loopGalleryNext(visibleSlides, slideWidth);
    } else if (dx > SWIPE_THRESHOLD && startedAtLeft) {
      this.loopGalleryPrevious(visibleSlides, slideWidth);
    }
  }

  /**
   * [AI] 2026-05-18: requestAnimationFrame ベースの手動 scrollLeft アニメーション。
   * scroll-snap や scrollTo({ behavior: 'smooth' }) の競合に左右されず一定の
   * easing / duration で必ず最後まで実行されることを保証する。
   */
  animateGalleryScroll(targetLeft, duration) {
    if (!this.slidesContainer) return Promise.resolve();
    const start = this.slidesContainer.scrollLeft;
    const change = targetLeft - start;
    if (change === 0) return Promise.resolve();
    const startTime = performance.now();
    return new Promise((resolve) => {
      const step = (now) => {
        const elapsed = now - startTime;
        const t = Math.min(1, elapsed / duration);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - t, 3);
        this.slidesContainer.scrollLeft = start + change * eased;
        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          resolve();
        }
      };
      requestAnimationFrame(step);
    });
  }

  /**
   * [AI] 2026-05-18: 末尾 → 先頭のループ。slide[0] のクローンを末尾に append し、
   * 手動アニメーションで scrollLeft を clone 位置へ移し、着地後に瞬時に 0 へ戻す。
   * scroll-snap を一時無効化することで毎回スムーズなアニメーションを保証する。
   */
  async loopGalleryNext(visibleSlides, slideWidth) {
    if (this._galleryLooping) return;
    this._galleryLooping = true;
    const firstSlide = visibleSlides[0];
    const clone = firstSlide.cloneNode(true);
    clone.dataset.galleryClone = 'next';
    clone.setAttribute('aria-hidden', 'true');
    this.slidesContainer.appendChild(clone);

    // scroll-snap がアニメーションを横取りしないよう一時無効化
    const prevSnap = this.slidesContainer.style.scrollSnapType;
    this.slidesContainer.style.scrollSnapType = 'none';

    const targetPos = visibleSlides.length * slideWidth;
    await this.animateGalleryScroll(targetPos, 500);

    // active 状態を更新（着地時にチラつかせない）
    const firstIdx = this.slides.indexOf(firstSlide);
    this.activeSlideIndex = firstIdx;
    this.dots.forEach((d, i) => d.classList.toggle('is-active', i === firstIdx));
    this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === firstIdx));

    // 瞬時に 0 に戻す + clone 撤去（同一 JS task 内で完結するので paint されない）
    this.slidesContainer.scrollLeft = 0;
    clone.remove();

    // 次フレームで scroll-snap を復元（snap が即座に戻りを上書きしないよう待つ）
    requestAnimationFrame(() => {
      this.slidesContainer.style.scrollSnapType = prevSnap;
      this._galleryLooping = false;
    });
  }

  /**
   * [AI] 2026-05-18: 先頭 → 末尾のループ。slide[最後] のクローンを先頭に prepend し、
   * scrollLeft を slideWidth に補正してから手動アニメーションで 0 に向かう。
   * 着地後に real 最終スライドの位置へ瞬時に戻す。
   */
  async loopGalleryPrevious(visibleSlides, slideWidth) {
    if (this._galleryLooping) return;
    this._galleryLooping = true;
    const lastSlide = visibleSlides[visibleSlides.length - 1];
    const clone = lastSlide.cloneNode(true);
    clone.dataset.galleryClone = 'prev';
    clone.setAttribute('aria-hidden', 'true');

    const prevSnap = this.slidesContainer.style.scrollSnapType;
    this.slidesContainer.style.scrollSnapType = 'none';

    this.slidesContainer.insertBefore(clone, this.slidesContainer.firstChild);
    // prepend で全スライドが右に slideWidth ずれたぶんを補正
    this.slidesContainer.scrollLeft = slideWidth;

    await this.animateGalleryScroll(0, 500);

    const lastIdx = this.slides.indexOf(lastSlide);
    this.activeSlideIndex = lastIdx;
    this.dots.forEach((d, i) => d.classList.toggle('is-active', i === lastIdx));
    this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === lastIdx));

    // clone がまだ位置 0 にいる状態で real 最後の位置（= visibleSlides.length * slideWidth）に飛ぶ
    this.slidesContainer.scrollLeft = visibleSlides.length * slideWidth;
    clone.remove();
    // clone 削除で全体が左に slideWidth ずれるので最終位置を補正
    this.slidesContainer.scrollLeft = (visibleSlides.length - 1) * slideWidth;

    requestAnimationFrame(() => {
      this.slidesContainer.style.scrollSnapType = prevSnap;
      this._galleryLooping = false;
    });
  }

  /**
   * [AI] 2026-05-11: Phase B-3 共有 URL コピー
   * Web Share API がモバイルで使えるならそちらを優先、それ以外は clipboard にコピー。
   */
  async handleShareCopy() {
    if (!this.shareCopyBtn) return;
    const url = this.shareCopyBtn.getAttribute('data-url') || window.location.href;
    const title = (this.querySelector('.mgh-main-product__title') || {}).textContent || document.title;
    if (navigator.share && window.matchMedia('(max-width: 989px)').matches) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        // ユーザーがキャンセル等 → clipboard コピーへフォールバック
      }
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch (error) {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
    }
    if (this.shareCopyMsg) {
      this.shareCopyMsg.hidden = false;
      window.clearTimeout(this._shareCopyTimer);
      this._shareCopyTimer = window.setTimeout(() => {
        this.shareCopyMsg.hidden = true;
      }, 1500);
    }
  }

  /**
   * [AI] 2026-05-11: PG-003 画像拡大モーダル（メイン画像 + サムネ切替）
   */
  openZoom(slide) {
    if (!this.zoomModal || !this.zoomImage || !slide) return;
    // Collect only visible (non-hidden) slides for the current color filter
    const visibleSlides = this.slides.filter((s) => !s.hasAttribute('hidden'));
    if (visibleSlides.length === 0) return;
    const initialIdx = visibleSlides.indexOf(slide);
    this._zoomVisibleSlides = visibleSlides;
    this._zoomActiveIdx = initialIdx >= 0 ? initialIdx : 0;
    this.renderZoomThumbs(visibleSlides, this._zoomActiveIdx);
    this.setZoomImageFromSlide(visibleSlides[this._zoomActiveIdx]);
    this.updateZoomNavState();

    this._zoomReturnFocus = document.activeElement;
    this.zoomModal.removeAttribute('hidden');
    this.zoomModal.setAttribute('aria-hidden', 'false');
    this.lockBodyScroll();
    this.focusFirstIn(this.zoomModal);
    this._zoomEscHandler = (event) => {
      if (event.key === 'Escape') this.closeZoom();
      else if (event.key === 'ArrowLeft') this.zoomNavigate(-1);
      else if (event.key === 'ArrowRight') this.zoomNavigate(1);
    };
    document.addEventListener('keydown', this._zoomEscHandler);
  }

  zoomNavigate(delta) {
    const slides = this._zoomVisibleSlides || [];
    if (slides.length === 0) return;
    const next = this._zoomActiveIdx + delta;
    if (next < 0 || next >= slides.length) return;
    this._zoomActiveIdx = next;
    this.setZoomImageFromSlide(slides[next]);
    if (this.zoomThumbsContainer) {
      Array.from(this.zoomThumbsContainer.children).forEach((btn, i) => {
        btn.classList.toggle('is-active', i === next);
      });
      const activeBtn = this.zoomThumbsContainer.children[next];
      if (activeBtn && activeBtn.scrollIntoView) {
        activeBtn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
    this.updateZoomNavState();
  }

  updateZoomNavState() {
    const slides = this._zoomVisibleSlides || [];
    if (this.zoomPrev) this.zoomPrev.disabled = this._zoomActiveIdx <= 0;
    if (this.zoomNext) this.zoomNext.disabled = this._zoomActiveIdx >= slides.length - 1;
  }

  setZoomImageFromSlide(slide) {
    if (!slide || !this.zoomImage) return;
    const img = slide.querySelector('img');
    if (!img) return;
    let src = img.src;
    if (img.srcset) {
      const lastEntry = img.srcset.split(',').pop();
      if (lastEntry) {
        const url = lastEntry.trim().split(' ')[0];
        if (url) src = url;
      }
    }
    this.zoomImage.src = src;
    this.zoomImage.alt = img.alt || '';
    // 差し替え先の縦横比で領域を確保し直す（表示サイズは CSS が決める）
    const naturalWidth = img.naturalWidth || img.getAttribute('width');
    const naturalHeight = img.naturalHeight || img.getAttribute('height');
    if (naturalWidth && naturalHeight) {
      this.zoomImage.setAttribute('width', naturalWidth);
      this.zoomImage.setAttribute('height', naturalHeight);
    }
  }

  renderZoomThumbs(visibleSlides, activeIdx) {
    if (!this.zoomThumbsContainer) return;
    this.zoomThumbsContainer.innerHTML = '';
    visibleSlides.forEach((slide, idx) => {
      const img = slide.querySelector('img');
      if (!img) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mgh-main-product__zoom-thumb';
      if (idx === activeIdx) button.classList.add('is-active');
      button.setAttribute('aria-label', img.alt || '');
      const thumbImg = document.createElement('img');
      thumbImg.src = img.src;
      thumbImg.alt = img.alt || '';
      thumbImg.loading = 'lazy';
      button.appendChild(thumbImg);
      button.addEventListener('click', () => this.handleZoomThumbClick(idx));
      this.zoomThumbsContainer.appendChild(button);
    });
  }

  handleZoomThumbClick(idx) {
    const slides = this._zoomVisibleSlides || [];
    if (idx < 0 || idx >= slides.length) return;
    this._zoomActiveIdx = idx;
    this.setZoomImageFromSlide(slides[idx]);
    if (this.zoomThumbsContainer) {
      Array.from(this.zoomThumbsContainer.children).forEach((btn, i) => {
        btn.classList.toggle('is-active', i === idx);
      });
    }
    this.updateZoomNavState();
  }

  /**
   * [AI] 2026-05-11: PG-003 Find My Size ドロワー & サイズレコメンド
   */
  openFindSize() {
    if (!this.findSizeDrawer) return;
    this._findSizeReturnFocus = document.activeElement;
    this.findSizeDrawer.removeAttribute('hidden');
    requestAnimationFrame(() => this.findSizeDrawer.classList.add('is-open'));
    this.lockBodyScroll();
    this.focusFirstIn(this.findSizeDrawer);
    this._findSizeEscHandler = (event) => {
      if (event.key === 'Escape') this.closeFindSize();
    };
    document.addEventListener('keydown', this._findSizeEscHandler);
  }

  closeFindSize() {
    if (!this.findSizeDrawer) return;
    const wasOpen = this.findSizeDrawer.classList.contains('is-open');
    this.findSizeDrawer.classList.remove('is-open');
    if (wasOpen) this.unlockBodyScroll();
    if (this._findSizeEscHandler) {
      document.removeEventListener('keydown', this._findSizeEscHandler);
      this._findSizeEscHandler = null;
    }
    if (this._findSizeReturnFocus && typeof this._findSizeReturnFocus.focus === 'function') {
      this._findSizeReturnFocus.focus();
      this._findSizeReturnFocus = null;
    }
    window.setTimeout(() => {
      if (this.findSizeDrawer && !this.findSizeDrawer.classList.contains('is-open')) {
        this.findSizeDrawer.setAttribute('hidden', '');
      }
    }, 300);
  }

  bindFindSizeButtonGroup(buttonSelector, hiddenInputSelector) {
    const buttons = this.querySelectorAll(buttonSelector);
    const hidden = this.querySelector(hiddenInputSelector);
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const attrName = btn.getAttributeNames().find((n) => n.startsWith('data-mgh-pp-find-size-'));
        const value = btn.getAttribute(attrName);
        buttons.forEach((b) => {
          const active = b === btn;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-checked', active ? 'true' : 'false');
        });
        if (hidden) hidden.value = value;
      });
    });
  }

  handleFindSizeSubmit(event) {
    event.preventDefault();
    const heightEl = this.querySelector('[data-mgh-pp-find-size-height]');
    const weightEl = this.querySelector('[data-mgh-pp-find-size-weight]');
    if (!heightEl || !weightEl) return;
    const height = parseInt(heightEl.value, 10);
    const weight = parseInt(weightEl.value, 10);
    const usual = (this.querySelector('[data-mgh-pp-find-size-usual-input]') || {}).value || '';
    const fit = (this.querySelector('[data-mgh-pp-find-size-fit-input]') || {}).value || 'regular';
    if (!height || !weight || !usual) return;
    const result = this.calculateRecommendedSize({ height, weight, usual, fit });
    this.renderFindSizeResult(result, { height, weight, usual, fit });
  }

  calculateRecommendedSize({ height, weight, usual, fit }) {
    const chart = (this.findSizeConfig && this.findSizeConfig.chart) || this.defaultSizeChart();
    const available = (this.findSizeConfig && this.findSizeConfig.available_sizes) || [];

    // 1) Find best chart entry by height/weight (closest center match)
    let baseSize = usual;
    let bestScore = Infinity;
    chart.forEach((entry) => {
      const hCenter = (entry.height_min + entry.height_max) / 2;
      const wCenter = (entry.weight_min + entry.weight_max) / 2;
      const score = Math.abs(height - hCenter) + Math.abs(weight - wCenter);
      const inRange = height >= entry.height_min && height <= entry.height_max
        && weight >= entry.weight_min && weight <= entry.weight_max;
      if (inRange && score < bestScore) {
        bestScore = score;
        baseSize = entry.size;
      }
    });
    if (bestScore === Infinity) {
      // No exact match — fall back to closest center
      chart.forEach((entry) => {
        const hCenter = (entry.height_min + entry.height_max) / 2;
        const wCenter = (entry.weight_min + entry.weight_max) / 2;
        const score = Math.abs(height - hCenter) + Math.abs(weight - wCenter);
        if (score < bestScore) {
          bestScore = score;
          baseSize = entry.size;
        }
      });
    }

    // [AI] 2026-06-01: チャート由来の baseSize が在庫サイズに無い場合（例: women は S スタートで
    // XS 無しなのに既定チャートが XS を返す）、最寄りの在庫サイズに丸める。これにより
    // 「存在しない＝買えないサイズ」を推奨しない。
    const clampedBase = this.clampToAvailableSize(baseSize, available);

    // 2) Adjust by fit preference (slim: -1, regular: 0, relaxed: +1)
    const fitDelta = fit === 'slim' ? -1 : fit === 'relaxed' ? 1 : 0;
    const finalSize = this.applySizeDelta(clampedBase, fitDelta, available);
    return finalSize;
  }

  // [AI] 2026-06-01: アパレルサイズを在庫サイズの最寄りに丸める。
  // size と available の双方がアパレル表記(XS〜XXL)のときのみ丸める。
  // 数値サイズ（靴等）や在庫情報なしの場合は元の size をそのまま返す（挙動を変えない）。
  clampToAvailableSize(size, available) {
    if (!available || available.length === 0) return size;
    if (available.indexOf(size) >= 0) return size;
    const order = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
    const rank = order.indexOf(size);
    if (rank < 0) return size;
    let best = null;
    let bestDiff = Infinity;
    available.forEach((a) => {
      const ar = order.indexOf(a);
      if (ar < 0) return;
      const diff = Math.abs(ar - rank);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = a;
      }
    });
    return best || size;
  }

  applySizeDelta(currentSize, delta, available) {
    if (!available || available.length === 0) return currentSize;
    const idx = available.indexOf(currentSize);
    if (idx < 0) return currentSize;
    const nextIdx = Math.max(0, Math.min(available.length - 1, idx + delta));
    return available[nextIdx];
  }

  defaultSizeChart() {
    // Default size chart for apparel (cm / kg)
    return [
      { size: 'XS', height_min: 150, height_max: 160, weight_min: 40, weight_max: 50 },
      { size: 'S', height_min: 155, height_max: 168, weight_min: 47, weight_max: 58 },
      { size: 'M', height_min: 162, height_max: 175, weight_min: 55, weight_max: 68 },
      { size: 'L', height_min: 170, height_max: 183, weight_min: 65, weight_max: 80 },
      { size: 'XL', height_min: 178, height_max: 192, weight_min: 75, weight_max: 92 },
      { size: 'XXL', height_min: 185, height_max: 200, weight_min: 85, weight_max: 110 },
    ];
  }

  renderFindSizeResult(size, params) {
    if (!this.findSizeResult) return;
    const fitLabel = params.fit === 'slim' ? (this.labels.fit_slim || 'Slim')
      : params.fit === 'relaxed' ? (this.labels.fit_relaxed || 'Relaxed')
      : (this.labels.fit_regular || 'Regular');
    const resultLabel = this.labels.find_my_size_result_label || 'Recommended size: ';
    const notePrefix = this.labels.find_my_size_note_prefix || '';
    const applyLabel = this.labels.find_my_size_apply || 'Select this size';
    const noteText = notePrefix + params.height + 'cm / ' + params.weight + 'kg / ' + params.usual + ' / ' + fitLabel;

    this.findSizeResult.innerHTML = '';
    const sizeEl = document.createElement('p');
    sizeEl.className = 'mgh-main-product__find-size-result-size';
    sizeEl.textContent = resultLabel + size;
    const noteEl = document.createElement('p');
    noteEl.className = 'mgh-main-product__find-size-result-note';
    noteEl.textContent = noteText;

    // Only show apply button if the size exists among product's available sizes
    const sizeBtn = this.sizeButtons.find((b) => b.dataset.mghPpSize === size);
    let applyBtn = null;
    if (sizeBtn) {
      applyBtn = document.createElement('button');
      applyBtn.type = 'button';
      applyBtn.className = 'mgh-main-product__find-size-apply';
      applyBtn.textContent = applyLabel;
      applyBtn.addEventListener('click', () => {
        sizeBtn.click();
        this.closeFindSize();
      });
    }

    this.findSizeResult.appendChild(sizeEl);
    this.findSizeResult.appendChild(noteEl);

    // [AI] 2026-05-21: 商品個別の size_chart metafield があれば、推奨サイズの採寸値を併記
    const sizeChart = this.findSizeConfig && this.findSizeConfig.size_chart;
    if (sizeChart && Array.isArray(sizeChart.rows) && Array.isArray(sizeChart.columns)) {
      const row = sizeChart.rows.find((r) => r && r.size === size);
      if (row && Array.isArray(row.values)) {
        const measurementsEl = document.createElement('p');
        measurementsEl.className = 'mgh-main-product__find-size-result-measurements';
        const unit = sizeChart.unit || '';
        const parts = sizeChart.columns.map((col, idx) => {
          const v = row.values[idx];
          if (v === undefined || v === null || v === '') return null;
          const suffix = typeof v === 'number' ? unit : '';
          return col.label + ' ' + v + suffix;
        }).filter(Boolean);
        measurementsEl.textContent = parts.join(' / ');
        this.findSizeResult.appendChild(measurementsEl);
      }
    }

    if (applyBtn) this.findSizeResult.appendChild(applyBtn);
    this.findSizeResult.removeAttribute('hidden');
  }

  /**
   * [AI] 2026-05-11: PG-003 Size Guide ドロワー
   */
  openSizeGuide() {
    if (!this.sizeGuideDrawer || !this.sizeGuideTrigger) return;
    this._sizeGuideReturnFocus = document.activeElement;
    this.sizeGuideDrawer.removeAttribute('hidden');
    requestAnimationFrame(() => this.sizeGuideDrawer.classList.add('is-open'));
    this.lockBodyScroll();
    this.focusFirstIn(this.sizeGuideDrawer);
    this._sizeGuideEscHandler = (event) => {
      if (event.key === 'Escape') this.closeSizeGuide();
    };
    document.addEventListener('keydown', this._sizeGuideEscHandler);
  }

  closeSizeGuide() {
    if (!this.sizeGuideDrawer) return;
    const wasOpen = this.sizeGuideDrawer.classList.contains('is-open');
    this.sizeGuideDrawer.classList.remove('is-open');
    if (wasOpen) this.unlockBodyScroll();
    if (this._sizeGuideEscHandler) {
      document.removeEventListener('keydown', this._sizeGuideEscHandler);
      this._sizeGuideEscHandler = null;
    }
    if (this._sizeGuideReturnFocus && typeof this._sizeGuideReturnFocus.focus === 'function') {
      this._sizeGuideReturnFocus.focus();
      this._sizeGuideReturnFocus = null;
    }
    window.setTimeout(() => {
      if (this.sizeGuideDrawer && !this.sizeGuideDrawer.classList.contains('is-open')) {
        this.sizeGuideDrawer.setAttribute('hidden', '');
      }
    }, 300);
  }

  closeZoom() {
    if (!this.zoomModal) return;
    const wasOpen = !this.zoomModal.hasAttribute('hidden');
    this.zoomModal.setAttribute('hidden', '');
    this.zoomModal.setAttribute('aria-hidden', 'true');
    if (wasOpen) this.unlockBodyScroll();
    if (this._zoomEscHandler) {
      document.removeEventListener('keydown', this._zoomEscHandler);
      this._zoomEscHandler = null;
    }
    if (this._zoomReturnFocus && typeof this._zoomReturnFocus.focus === 'function') {
      this._zoomReturnFocus.focus();
      this._zoomReturnFocus = null;
    }
  }

  /**
   * [AI] 2026-05-11: PG-003 モバイル Sticky CTA。
   * Main CTA がビューポート外に出たら sticky を表示。
   * ボタンクリックで main CTA の click をトリガー（フォーム送信ロジック共有）。
   */
  setupStickyCta() {
    if (typeof IntersectionObserver === 'undefined') return;
    this.stickyButton.addEventListener('click', () => {
      if (this.cta) this.cta.click();
    });
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // Show sticky ONLY when main CTA has been scrolled past (above viewport),
        // not when it's still below (user hasn't reached it yet).
        const ctaIsAbove = entry.boundingClientRect.top < 0;
        const shouldShow = !entry.isIntersecting && ctaIsAbove;
        this.stickyCta.classList.toggle('is-visible', shouldShow);
        this.stickyCta.toggleAttribute('hidden', !shouldShow);
        this.stickyCta.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      });
    }, { threshold: 0 });
    observer.observe(this.cta);
    this._stickyObserver = observer;
  }

  /**
   * [AI] 2026-05-11: PG-003 カラー別画像フィルタ
   * data-colors 属性が空 (= 全カラー共通) または現在のカラーを含む要素のみ表示。
   * フィルタ後、最初の visible スライドにアクティブを移動。
   */
  filterGalleryByColor(colorName) {
    if (!colorName) return;
    const marker = '|' + colorName + '|';
    const matches = (el) => {
      const colors = el.getAttribute('data-colors') || '';
      return colors === '' || colors.includes(marker);
    };

    this.thumbs.forEach((thumb) => {
      const li = thumb.closest('li') || thumb;
      li.toggleAttribute('hidden', !matches(li));
    });
    this.slides.forEach((slide) => {
      slide.toggleAttribute('hidden', !matches(slide));
    });
    this.dots.forEach((dot) => {
      dot.toggleAttribute('hidden', !matches(dot));
    });

    const firstVisibleIdx = this.slides.findIndex((s) => !s.hasAttribute('hidden'));
    if (firstVisibleIdx >= 0) {
      this.activeSlideIndex = firstVisibleIdx;
      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === firstVisibleIdx));
      this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === firstVisibleIdx));
      if (this.slidesContainer && this.slides[firstVisibleIdx]) {
        this.slidesContainer.scrollTo({ left: this.slides[firstVisibleIdx].offsetLeft, behavior: 'instant' });
      }
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this._ctaResetTimer);
    if (this._stickyObserver) {
      this._stickyObserver.disconnect();
      this._stickyObserver = null;
    }
    if (this._zoomEscHandler) {
      document.removeEventListener('keydown', this._zoomEscHandler);
      this._zoomEscHandler = null;
      this.unlockBodyScroll();
    }
    if (this._sizeGuideEscHandler) {
      document.removeEventListener('keydown', this._sizeGuideEscHandler);
      this._sizeGuideEscHandler = null;
      this.unlockBodyScroll();
    }
    if (this._findSizeEscHandler) {
      document.removeEventListener('keydown', this._findSizeEscHandler);
      this._findSizeEscHandler = null;
      this.unlockBodyScroll();
    }
    // [AI] 2026-05-19: ギャラリースワイプ用 touch ハンドラのクリーンアップ。
    // テーマエディタ section reload 時の二重登録を防ぐ。
    if (this.slidesContainer) {
      if (this._galleryTouchStart) {
        this.slidesContainer.removeEventListener('touchstart', this._galleryTouchStart);
        this._galleryTouchStart = null;
      }
      if (this._galleryTouchEnd) {
        this.slidesContainer.removeEventListener('touchend', this._galleryTouchEnd);
        this._galleryTouchEnd = null;
      }
    }
  }
}

if (!customElements.get('mgh-main-product')) {
  customElements.define('mgh-main-product', MghMainProduct);
}
